import { z } from 'zod';
import { KPI_COLOR_TOKEN_ENUM } from '@nexus/kpi-formula';
import { NorthStarKpiSchema } from '../events/workspace.js';

export const PINNED_KPI_NAME = 'Spend';

/** Opaque catalog key for a user- or org-created KPI. Display copy lives on `displayName`. */
export function generateCustomKpiName(): string {
  return crypto.randomUUID();
}

export const KpiColorTokenSchema = z.enum(KPI_COLOR_TOKEN_ENUM);
export type KpiColorToken = z.infer<typeof KpiColorTokenSchema>;

export const KpiCatalogFormatSchema = z.enum(['CURRENCY', 'PERCENT', 'NUMBER']);
export type KpiCatalogFormat = z.infer<typeof KpiCatalogFormatSchema>;

/**
 * Whether a KPI value above zero is deemed positive or negative. Not a sort
 * direction — chart and table sorting are separate, client-owned state.
 */
export const KpiPolaritySchema = z.enum(['POSITIVE', 'NEGATIVE']);
export type KpiPolarity = z.infer<typeof KpiPolaritySchema>;

/** Inclusive bounds for KPI display precision across org and user settings. */
export const KPI_DECIMALS_MIN = 0;
export const KPI_DECIMALS_MAX = 4;

export const KpiCatalogEntrySchema = z.object({
  id: z.number().int(),
  name: z.string().min(1),
  displayName: z.string().min(1),
  formula: z.string().nullable(),
  format: KpiCatalogFormatSchema,
  polarity: KpiPolaritySchema,
  decimals: z.number().int().min(KPI_DECIMALS_MIN).max(KPI_DECIMALS_MAX),
  visible: z.boolean(),
  index: z.number().int(),
  colorToken: KpiColorTokenSchema.nullable(),
  isCustom: z.boolean(),
  isOverride: z.boolean(),
  isPinned: z.boolean(),
});
export type KpiCatalogEntry = z.infer<typeof KpiCatalogEntrySchema>;

export const KpiCatalogResponseSchema = z.array(KpiCatalogEntrySchema);
export type KpiCatalogResponse = z.infer<typeof KpiCatalogResponseSchema>;

export const CreateKpiBodySchema = z.object({
  displayName: z.string().min(1).max(255),
  formula: z.string().min(1).max(2000),
  format: KpiCatalogFormatSchema,
  polarity: KpiPolaritySchema,
  decimals: z.number().int().min(KPI_DECIMALS_MIN).max(KPI_DECIMALS_MAX).default(2),
  visible: z.boolean().optional().default(true),
  colorToken: KpiColorTokenSchema.optional(),
});
export type CreateKpiBody = z.infer<typeof CreateKpiBodySchema>;

export const UpdateKpiBodySchema = z.object({
  displayName: z.string().min(1).max(255).optional(),
  formula: z.string().min(1).max(2000).nullable().optional(),
  format: KpiCatalogFormatSchema.optional(),
  polarity: KpiPolaritySchema.optional(),
  decimals: z.number().int().min(KPI_DECIMALS_MIN).max(KPI_DECIMALS_MAX).optional(),
  visible: z.boolean().optional(),
  colorToken: KpiColorTokenSchema.optional(),
}).refine((body) => Object.keys(body).length > 0, { message: 'At least one field is required' });
export type UpdateKpiBody = z.infer<typeof UpdateKpiBodySchema>;

export const ReorderKpisBodySchema = z.object({
  names: z.array(z.string().min(1)).min(1),
});
export type ReorderKpisBody = z.infer<typeof ReorderKpisBodySchema>;

export const KpiIdParamsSchema = z.object({
  id: z.coerce.number().int().positive(),
});

/** Org KPIs tab: the tenant's Default NorthStar. `null` lets each user keep their own. */
export const TenantDefaultNorthStarSchema = z.object({
  defaultNorthStar: NorthStarKpiSchema.nullable(),
});
export type TenantDefaultNorthStar = z.infer<typeof TenantDefaultNorthStarSchema>;

/** My Brand dashboard KPI cards — web `card-prefs.test.ts` fails if CARD_DESCRIPTORS drifts from this. */
export const DASHBOARD_KPI_CARD_COUNT = 6;

const dashboardCardIndexSchema = z
  .number()
  .int()
  .min(0)
  .max(DASHBOARD_KPI_CARD_COUNT - 1);

function uniqueCardIndices(indices: number[]): boolean {
  return new Set(indices).size === indices.length;
}

/** Admin PUT body: full tenant layout (order + hidden set). */
export const TenantDashboardCardsSchema = z.object({
  order: z
    .array(dashboardCardIndexSchema)
    .length(DASHBOARD_KPI_CARD_COUNT, 'Order must include every card index')
    .refine(uniqueCardIndices, 'Duplicate card index in order'),
  disabled: z
    .array(dashboardCardIndexSchema)
    .max(DASHBOARD_KPI_CARD_COUNT - 1, 'At least one card must stay visible')
    .refine(uniqueCardIndices, 'Duplicate card index in disabled'),
});
export type TenantDashboardCards = z.infer<typeof TenantDashboardCardsSchema>;

/** Cap on distinct user-created formulas compiled into the tenant BigQuery union. */
export const MAX_TENANT_CUSTOM_KPIS = 50;

/** Sections a user KPI can be filed under in the settings grid. */
export const KPI_SECTION_IDS = [
  'keyPerformance',
  'engagementCreative',
  'costEfficiency',
  'scale',
  'other',
] as const;
export const KpiSectionIdSchema = z.enum(KPI_SECTION_IDS);
export type KpiSectionId = z.infer<typeof KpiSectionIdSchema>;

/** Saved section order and removed sections for one user in one tenant. */
export const KpiSectionLayoutSchema = z.object({
  order: z.array(KpiSectionIdSchema),
  removed: z.array(KpiSectionIdSchema),
});
export type KpiSectionLayout = z.infer<typeof KpiSectionLayoutSchema>;

export const UpdateKpiSectionLayoutBodySchema = KpiSectionLayoutSchema;
export type UpdateKpiSectionLayoutBody = KpiSectionLayout;

export const UserKpiEntrySchema = z.object({
  id: z.number().int(),
  name: z.string().min(1),
  displayName: z.string().min(1),
  formula: z.string().nullable(),
  format: KpiCatalogFormatSchema,
  polarity: KpiPolaritySchema,
  decimals: z.number().int().min(KPI_DECIMALS_MIN).max(KPI_DECIMALS_MAX),
  index: z.number().int(),
  hidden: z.boolean(),
  colorToken: KpiColorTokenSchema,
  isCustom: z.boolean(),
  isShared: z.boolean(),
  canResetToOrg: z.boolean(),
  section: KpiSectionIdSchema.nullable(),
});
export type UserKpiEntry = z.infer<typeof UserKpiEntrySchema>;

export const UserKpiListResponseSchema = z.array(UserKpiEntrySchema);
export type UserKpiListResponse = z.infer<typeof UserKpiListResponseSchema>;

export const CreateUserKpiBodySchema = z.object({
  displayName: z.string().min(1).max(255),
  formula: z.string().min(1).max(2000),
  format: KpiCatalogFormatSchema,
  polarity: KpiPolaritySchema,
  decimals: z.number().int().min(KPI_DECIMALS_MIN).max(KPI_DECIMALS_MAX).default(2),
  colorToken: KpiColorTokenSchema.optional(),
  hidden: z.boolean().optional().default(false),
  shareWithOrg: z.boolean().optional().default(false),
  section: KpiSectionIdSchema.optional(),
});
export type CreateUserKpiBody = z.infer<typeof CreateUserKpiBodySchema>;

export const UpdateUserKpiBodySchema = z.object({
  displayName: z.string().min(1).max(255).optional(),
  formula: z.string().min(1).max(2000).nullable().optional(),
  format: KpiCatalogFormatSchema.optional(),
  polarity: KpiPolaritySchema.optional(),
  decimals: z.number().int().min(KPI_DECIMALS_MIN).max(KPI_DECIMALS_MAX).optional(),
  hidden: z.boolean().optional(),
  colorToken: KpiColorTokenSchema.optional(),
}).refine((body) => Object.keys(body).length > 0, { message: 'At least one field is required' });
export type UpdateUserKpiBody = z.infer<typeof UpdateUserKpiBodySchema>;

export const ReorderUserKpisBodySchema = ReorderKpisBodySchema;
export type ReorderUserKpisBody = ReorderKpisBody;
